<template>
  <transition name="modal-fade">
    <div class="modal-overlay" @click.self="close">
      <div class="modal-box">

        <!-- Modal header -->
        <div class="modal-hdr">
          <div class="modal-hdr__icon"><i class="fa fa-archive"></i></div>
          <div class="modal-hdr__text">
            <div class="modal-hdr__title">{{ is_edit ? 'Edit Archival' : 'Add Archival' }}</div>
            <div class="modal-hdr__sub">{{ is_edit ? 'Update plan details' : 'Create a new archival plan' }}</div>
          </div>
          <button class="modal-close" @click="close"><i class="fa fa-times"></i></button>
        </div>

        <!-- Modal body -->
        <div class="modal-body">

          <div class="field-row">
            <div class="field-group" :class="{ 'field-group--error': errors['name'] }">
              <label class="field-label">Archival Name <span class="req">*</span></label>
              <input v-model="archival.name" type="text" class="field-input" placeholder="Enter archival name" />
              <span class="field-error" v-if="errors['name']">{{ errors['name'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['duration'] }">
              <label class="field-label">Duration (Years) <span class="req">*</span></label>
              <input v-model="archival.duration" type="number" min="1" class="field-input" placeholder="e.g. 5" />
              <span class="field-error" v-if="errors['duration']">{{ errors['duration'][0] }}</span>
            </div>
          </div>

          <div class="field-row field-row--4">
            <div class="field-group" :class="{ 'field-group--error': errors['default_rate'] }">
              <label class="field-label">Default Rate <span class="req">*</span></label>
              <div class="input-prefix">
                <span class="input-prefix__sym">$</span>
                <input v-model="archival.default_rate" type="number" min="0" class="field-input field-input--prefixed" placeholder="0.00" />
              </div>
            </div>

            <div class="field-group">
              <label class="field-label">Additional Rate</label>
              <div class="input-checkbox-row">
                <label class="mini-toggle">
                  <input type="checkbox" v-model="archival.has_additional_rate" />
                  <span class="mini-toggle__track"><span class="mini-toggle__thumb"></span></span>
                </label>
                <input
                  v-model="archival.additional_rate"
                  type="text"
                  class="field-input"
                  :disabled="!archival.has_additional_rate"
                  placeholder="0.00"
                  :class="{ 'field-input--disabled': !archival.has_additional_rate }"
                />
              </div>
            </div>

            <div class="field-group">
              <label class="field-label">Tax (%)</label>
              <div class="input-suffix">
                <input v-model="archival.tax_percentage" type="number" min="0" class="field-input field-input--suffixed" placeholder="0" />
                <span class="input-suffix__sym">%</span>
              </div>
            </div>

            <div class="field-group">
              <label class="field-label">Commission ($)</label>
              <div class="input-prefix">
                <span class="input-prefix__sym">$</span>
                <input v-model="archival.commission_percentage" type="number" min="0" class="field-input field-input--prefixed" placeholder="0.00" />
              </div>
            </div>
          </div>

        </div>

        <!-- Modal footer -->
        <div class="modal-ftr">
          <button class="ftr-cancel" @click="close">Cancel</button>
          <button class="ftr-save" @click="save">
            <i class="fa fa-check"></i> {{ is_edit ? 'Update' : 'Save' }}
          </button>
        </div>

      </div>
    </div>
  </transition>
</template>

<script>
import { CREATE_ARCHIVAL } from "@/core/services/store/actions.type";

export default {
  props: {
    showModal:  { type: Boolean, default: false },
    is_edit:    { type: Boolean, default: false },
    archival: {
      type: Object,
      default: () => ({ name: "", id: 0, default_rate: 0, duration: 0, additional_rate: 0, commission_percentage: 0, tax_percentage: 0, has_additional_rate: false }),
    },
  },
  data() {
    return { errors: [] };
  },
  methods: {
    close() {
      this.$emit("closemodal");
    },
    save() {
      this.$store.dispatch(CREATE_ARCHIVAL, this.archival)
        .then(() => {
          this.$toastr.s(this.is_edit ? "Archival successfully updated" : "Archival successfully created");
          this.$emit("closemodal");
        })
        .catch(err => { this.errors = err; });
    },
  },
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$dark:   #0d1b3e;
$border: #dde2ec;

/* ── overlay ── */
.modal-overlay {
  position: fixed; inset: 0; z-index: 1050;
  background: rgba(13,27,62,0.45);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
}

.modal-box {
  background: #fff;
  border-radius: 16px;
  width: 100%; max-width: 560px;
  box-shadow: 0 24px 64px rgba(13,27,62,0.22);
  overflow: hidden;
}

/* ── header ── */
.modal-hdr {
  display: flex; align-items: center; gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);

  &__icon {
    width: 40px; height: 40px; border-radius: 10px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 16px; flex-shrink: 0;
  }
  &__text  { flex: 1; }
  &__title { font-size: 15px; font-weight: 700; color: $dark; line-height: 1.2; }
  &__sub   { font-size: 12px; color: #7a8499; margin-top: 2px; }
}

.modal-close {
  width: 30px; height: 30px; border-radius: 7px;
  border: none; background: transparent; color: #a0aab8;
  font-size: 14px; cursor: pointer; display: flex;
  align-items: center; justify-content: center;
  transition: background 0.15s;
  &:hover { background: #f0f3fa; color: $dark; }
}

/* ── body ── */
.modal-body {
  padding: 20px;
  display: flex; flex-direction: column; gap: 14px;
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  &--4 { grid-template-columns: repeat(4, 1fr); }
}

.field-group {
  display: flex; flex-direction: column; gap: 5px;
  &--error .field-input { border-color: #b42318; }
}

.field-label {
  font-size: 11px; font-weight: 600;
  color: #5a6278; font-family: $font;
  .req { color: #b42318; }
}

.field-input {
  height: 36px;
  border: 1.5px solid $border; border-radius: 8px;
  padding: 0 10px; font-size: 13px;
  color: $dark; font-family: $font;
  outline: none; background: #fff; width: 100%;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus { border-color: $blue; box-shadow: 0 0 0 3px rgba(56,154,212,0.1); }
  &::placeholder { color: #b0bac9; }
  &--prefixed  { padding-left: 28px; }
  &--suffixed  { padding-right: 28px; }
  &--disabled  { background: #f4f6fb; color: #a0aab8; }
}

.field-error { font-size: 11px; color: #b42318; }

/* ── prefix / suffix ── */
.input-prefix, .input-suffix {
  position: relative;
}

.input-prefix__sym {
  position: absolute; left: 9px; top: 50%;
  transform: translateY(-50%);
  font-size: 12px; color: #7a8499; pointer-events: none;
}

.input-suffix__sym {
  position: absolute; right: 9px; top: 50%;
  transform: translateY(-50%);
  font-size: 12px; color: #7a8499; pointer-events: none;
}

/* ── checkbox + input row ── */
.input-checkbox-row {
  display: flex; align-items: center; gap: 8px;
}

/* mini toggle */
.mini-toggle {
  position: relative; cursor: pointer; flex-shrink: 0;
  input { display: none; }

  &__track {
    display: block; width: 34px; height: 18px;
    background: #d0d7e3; border-radius: 9px;
    position: relative; transition: background 0.2s;
  }

  &__thumb {
    position: absolute; top: 2px; left: 2px;
    width: 14px; height: 14px;
    background: #fff; border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0,0,0,0.18);
    transition: transform 0.2s;
  }

  input:checked ~ &__track { background: $blue; }
  input:checked ~ &__track &__thumb { transform: translateX(16px); }
}

/* ── footer ── */
.modal-ftr {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid $border;
  background: #fafbfd;
}

.ftr-cancel {
  height: 36px; padding: 0 18px; border-radius: 8px;
  border: 1.5px solid $border; background: #fff;
  color: $dark; font-size: 13px; font-weight: 600;
  font-family: $font; cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #f4f6fb; }
}

.ftr-save {
  height: 36px; padding: 0 20px; border-radius: 8px;
  border: none; background: $blue; color: #fff;
  font-size: 13px; font-weight: 600; font-family: $font;
  cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
  transition: background 0.15s, box-shadow 0.15s;
  &:hover { background: #2d85bb; box-shadow: 0 4px 12px rgba(56,154,212,0.35); }
  i { font-size: 12px; }
}

.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.2s; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }
</style>
